export const agenciesData = {
  industry: "Agencies",
  slug: "agencies",
  
  // SEO Meta Tags
  seo: {
    title: "AI Intake Forms for Agencies | HeyIntake",
    description: "AI-powered intake forms for marketing and creative agencies. Qualify leads, scope projects accurately, and collect brand assets before kickoff.",
    keywords: [
      "agency client intake form",
      "creative agency lead qualification",
      "marketing agency onboarding",
      "project scoping software",
      "agency client onboarding",
      "creative project intake"
    ]
  },

  heroTitle: "Client Intake Forms for Creative & Marketing Agencies",
  heroSubtitle: "Qualify leads, scope projects accurately, and onboard clients smoothly from day one.",
  heroDescription: "Whether you run a digital marketing agency, creative studio, PR firm, or full-service agency, HeyIntake helps you capture the right information to win and deliver great work.",
  
  // NEW: Why AI Scoring section
  whyAIScoring: {
    title: "Why Agencies Need Smarter Client Intake",
    intro: "Agency profitability depends on accurate scoping and efficient onboarding. Both start with better intake.",
    paragraphs: [
      "The agency new business process is notoriously inefficient. You invest hours in pitches for prospects who ghost you. You underquote projects because you didn't understand the full scope. You spend the first two weeks of every engagement chasing brand assets and credentials instead of doing billable work.",
      "HeyIntake transforms this process. Our AI-powered intake forms capture detailed project requirements, budget information, and brand assets before you even start the proposal. Every client arrives ready to go, with everything you need to hit the ground running.",
      "For marketing agencies, creative studios, PR firms, and digital agencies, this means higher proposal win rates, more accurate project estimates, and faster time-to-value for every new client engagement."
    ]
  },
  
  painPoints: [
    {
      title: "Scope Creep Nightmares",
      description: "Projects balloon beyond original estimates because requirements weren't captured clearly upfront."
    },
    {
      title: "Wasted Pitch Time",
      description: "Investing hours in proposals and pitches for prospects who can't afford your services."
    },
    {
      title: "Missing Brand Assets",
      description: "Chasing clients for logos, guidelines, access credentials, and assets after kickoff."
    },
    {
      title: "Unclear Success Metrics",
      description: "Starting projects without agreeing on what success looks like, leading to unhappy clients."
    },
    {
      title: "Slow Onboarding",
      description: "New client projects stall because you're waiting for information, approvals, or access."
    },
    {
      title: "No Lead Qualification",
      description: "Treating tire-kickers the same as serious buyers wastes your team's creative energy."
    }
  ],

  exampleFields: [
    {
      category: "Company Overview",
      fields: [
        "Company name and website",
        "Industry and target audience",
        "Company size and structure",
        "Key decision makers",
        "Current marketing/creative partners"
      ]
    },
    {
      category: "Project Scope",
      fields: [
        "What services are you looking for?",
        "Project goals and KPIs",
        "Target launch date",
        "Brand guidelines available?",
        "Existing assets to work with"
      ]
    },
    {
      category: "Budget & Timeline",
      fields: [
        "Approved budget range",
        "Budget approval status",
        "Project priority level",
        "Ongoing retainer vs. project basis"
      ]
    },
    {
      category: "Brand & Assets",
      fields: [
        "Logo files and brand colors",
        "Font licenses and guidelines",
        "Photography/video assets",
        "Social media access",
        "Analytics access"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Agency Intake Form",
    description: "Here's what an effective agency inquiry form looks like:",
    fields: [
      { label: "What services are you looking for?", type: "select", options: ["Branding/Identity", "Web Design/Development", "Marketing/Advertising", "Content/Social Media", "Video Production", "Full-service/Multiple"] },
      { label: "Describe your project", type: "textarea" },
      { label: "Company/Brand name", type: "text" },
      { label: "Project timeline", type: "select", options: ["ASAP", "1-2 months", "3-6 months", "Flexible"] },
      { label: "Project budget range", type: "select", options: ["$5K-$15K", "$15K-$30K", "$30K-$75K", "$75K-$150K", "$150K+"] },
      { label: "Is this a new brand or rebrand?", type: "select", options: ["New brand", "Rebrand/Refresh", "Expansion/Addition"] },
      { label: "Who's the decision-maker?", type: "select", options: ["I am", "Small team (2-3)", "Committee/Board", "Other"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // NEW: How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Agencies",
    subtitle: "From inquiry to project kickoff in three simple steps",
    steps: [
      {
        title: "Capture Project Details",
        description: "Prospects provide project scope, budget, timeline, and brand assets through your intake form."
      },
      {
        title: "AI Qualifies & Scores",
        description: "Our AI evaluates budget alignment, decision-maker involvement, and project readiness."
      },
      {
        title: "Start Projects Faster",
        description: "Win better projects and kick them off immediately with all assets in hand."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Lead & Project Scoring for Agencies",
    description: "HeyIntake's AI evaluates every inquiry to help you prioritize high-value prospects and identify red flags before you invest in pitching.",
    factors: [
      "Budget alignment with project scope",
      "Decision-maker involvement and approval status",
      "Timeline realism and urgency",
      "Clarity of goals and success metrics",
      "Asset and access readiness"
    ],
    exampleLead: {
      name: "TechStart Inc.",
      context: "SaaS Rebrand • $40K Budget • Q2 Launch",
      summary: "Series A startup, clear rebrand goals, budget approved by CEO, realistic 8-week timeline. All brand assets ready. Excellent fit for full rebrand package.",
      score: 91
    }
  },

  useCases: [
    {
      icon: "chart",
      title: "Scope Projects Accurately",
      description: "Capture detailed requirements upfront to create winning proposals without underquoting."
    },
    {
      icon: "brain",
      title: "Qualify Leads Automatically",
      description: "AI scoring identifies serious buyers with real budgets before you invest in pitching."
    },
    {
      icon: "file",
      title: "Collect Assets Before Kickoff",
      description: "Request brand guidelines, logins, and assets during intake so projects start smoothly."
    },
    {
      icon: "zap",
      title: "Speed Up Client Onboarding",
      description: "New projects launch faster when you already have everything you need to begin."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" },
    { name: "Intake Forms for Contractors", path: "/intake-form-for-contractors" }
  ],

  ctaText: "Ready to Win Better Projects and Onboard Faster?"
};
